import {
  Avatar,
  Button,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Paper,
  Toolbar,
  Typography
} from "@material-ui/core";
import { Delete, Edit, GetApp as GetAppIcon, Reply } from "@material-ui/icons";
import { useQuery } from "@redux-requests/react";
import { Form, Formik } from "formik";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { BaseLayout, Flaticon, Loader, NoQAData, PdfPreview } from "../components";
import EPostersGrid from "../components/EPostersGrid";
import { TextField } from "../components/formik";
import { types } from "../ducks";
import {
  createFavourite,
  createQuestionAnswer,
  deleteQuestionAnswer,
  getEPoster,
  getEPosterQAs,
  getRelatedEPosters
} from "../ducks/actions";
import { EditEposterDialog } from "../features/eposters";
import EditQuestionAnswerDialog from "../features/eposters/EditQuestionAnswerDialog";
import EPosterVideoUrls from "../features/eposters/EPosterVideoUrls";
import { useActionRequest, useCongress } from "../hooks";
import useMediaQuery from "../hooks/useMediaQuery";
import useUserMe from "../hooks/useUserMe";

const EPosterPage = () => {
  const isSmallScreen = useMediaQuery("(max-width: 767px)");
  const useStyles = makeStyles(theme => ({
    root: {
      "& > *": {
        marginBottom: theme.spacing(3)
      }
    },
    tagList: {
      "& > *": {
        marginRight: theme.spacing(1)
      }
    },
    headerContainer: {
      display: "flex",
      width: "100%",
      alignItems: "center",
      "& > :first-child": {
        flexGrow: 1
      },
      "& > :last-child > *": {
        marginLeft: theme.spacing(1)
      }
    },
    headbarItem: {
      marginLeft: isSmallScreen ? 0 : "auto"
    },
    qaActions: {
      "& > *": {
        marginRight: theme.spacing(1)
      }
    },
    textFormatted: {
      whiteSpace: "pre-line"
    }
  }));
  const intl = useIntl();
  const dispatch = useDispatch();
  const { id } = useParams();
  const classes = useStyles();
  useEffect(() => {
    dispatch(getEPoster({ id }));
    dispatch(getRelatedEPosters({ id }));
    dispatch(getEPosterQAs({ ePosterId: id }));
  }, [dispatch, id]);

  const { user } = useUserMe();

  const [editEposterDialogOpen, setEditEposterDialogOpen] = useState(false);

  const {
    data: { data: ePoster },
    loading: ePosterLoading
  } = useQuery({ type: types.GET_EPOSTER, defaultData: { data: {} } });

  const {
    data: { data: qas },
    loading: qasLoading
  } = useQuery({ type: types.GET_EPOSTER_QAS, defaultData: { data: [] } });

  const {
    data: { data: relatedEPosters },
    loading: relatedEPosterLoading
  } = useQuery({ type: types.GET_RELATED_EPOSTERS, defaultData: { data: [] } });

  const { congress } = useCongress();

  const firstDocument = ePoster.document || {};
  const isDownloadAllowed = ePoster.allowDownload;

  const publishedQas = _.filter(qas, { published: true });
  const unpublishedQas = _.filter(qas, { published: false });

  const createFavouriteRequest = useActionRequest({
    action: createFavourite
  });

  const createQuestionAnswerRequest = useActionRequest({
    action: createQuestionAnswer,
    successMessage:
      "Your question has been addressed to the author and it will be visible as soon as the author will reply"
  });

  const deleteQuestionAnswerRequest = useActionRequest({
    action: deleteQuestionAnswer
  });

  const [selectedQuestionAnswer, setSelectedQuestionAnswer] = useState(null);

  const onFavouriteCreate = () => {
    createFavouriteRequest({ favouritableType: "Eposter", favouritableId: id });
  };

  const onQuestionAnswerCreate = ({ question }, { resetForm }) => {
    createQuestionAnswerRequest({ ePosterId: id, question });
    resetForm();
  };

  const onQuestionAnswerDelete =
    ({ id }) =>
    () => {
      deleteQuestionAnswerRequest({ id });
    };

  const onQuestionAnswerDialogOpen =
    ({ questionAnswer }) =>
    () => {
      setSelectedQuestionAnswer(questionAnswer);
    };

  const onQuestionAnswerDialogClose = () => {
    setSelectedQuestionAnswer(null);
  };

  const onEditEposterDialogOpen = () => {
    setEditEposterDialogOpen(true);
  };
  const onEditEposterDialogClose = () => {
    setEditEposterDialogOpen(false);
  };

  return (
    <BaseLayout className={classes.root}>
      <Loader loading={ePosterLoading}>
        <PdfPreview documentUrl={firstDocument?.url} />
        <div>
          <Grid container>
            <Grid item xs={12} sm={6} md={6} lg={8}>
              <div>
                <Typography variant="h4">
                  <strong>{ePoster.title}</strong>
                </Typography>
                <Typography variant="h6">
                  <strong>{ePoster.subTitle}</strong>
                </Typography>
                <Typography variant="h6">
                  <i>
                    {_.uniqBy(ePoster.eposterAccademics, "profile.id")
                      .map(ea =>
                        `${ea.profile.title || ""} ${ea.profile.firstName} ${
                          ea.profile.lastName
                        }`.trim()
                      )
                      .join(", ")}
                  </i>
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={4}>
              <Toolbar>
                {_.find(ePoster.eposterAccademics, x => x.profile.id === user.profile?.id) ? (
                  <>
                    <Button
                      size="large"
                      variant="contained"
                      startIcon={<Edit />}
                      color="primary"
                      onClick={onEditEposterDialogOpen}
                      className={classes.headbarItem}
                    >
                      {intl.formatMessage({
                        id: "Pages.EPosterPage.EditEposter",
                        defaultMessage: "Edit E-Poster"
                      })}
                    </Button>

                    {editEposterDialogOpen && (
                      <EditEposterDialog onClose={onEditEposterDialogClose} />
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      startIcon={<GetAppIcon />}
                      size="large"
                      color="primary"
                      download
                      disabled={!firstDocument || !isDownloadAllowed}
                      href={firstDocument?.url}
                      target="_blank"
                    >
                      {intl.formatMessage({
                        id: "Pages.EPosterPage.Download",
                        defaultMessage: "Download"
                      })}
                    </Button>
                    <Button
                      size="large"
                      variant="contained"
                      startIcon={<Flaticon icon="star" />}
                      color="primary"
                      disabled={ePoster.isFavourite}
                      onClick={onFavouriteCreate}
                    >
                      {intl.formatMessage({
                        id: "Pages.EPosterPage.Favourites",
                        defaultMessage: "Favourites"
                      })}
                    </Button>
                  </>
                )}
              </Toolbar>
            </Grid>
          </Grid>
        </div>
        <Typography variant="h6">
          {ePoster.profiles
            ?.map(({ firstName, lastName }) => `${firstName} ${lastName}`)
            .join(", ")}
        </Typography>

        <EPosterVideoUrls ePoster={ePoster} />

        <Typography variant="body2" className={classes.textFormatted}>
          {ePoster.description}
        </Typography>
        <div className={classes.tagList}>
          {ePoster.tagList?.map(tag => (
            <Chip key={tag} label={tag} variant="outlined" />
          ))}
        </div>
      </Loader>
      <Divider />
      <Typography variant="h5">
        {intl.formatMessage({
          id: "Pages.EPosterPage.QuestionsAndAnswers",
          defaultMessage: "Questions and Answers"
        })}
      </Typography>
      <Loader loading={qasLoading}>
        {_.find(ePoster.eposterAccademics, x => x.profile.id === user.profile?.id) ? (
          <>
            <NoQAData data={publishedQas}>
              {publishedQas.map(({ id, question, answer, published, profile }) => (
                <div key={id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar src={profile?.avatarThumb}>
                        <Flaticon icon="question" />
                      </Avatar>
                    </ListItemAvatar>
                    <Typography variant="body1">
                      {`${profile?.firstName} ${profile?.lastName}:`} <strong>{question}</strong>
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar src={_.first(ePoster.eposterAccademics)?.profile?.avatarThumb} />
                    </ListItemAvatar>
                    <Typography variant="body2">
                      {`${_.first(ePoster.eposterAccademics)?.profile.firstName} ${
                        _.first(ePoster.eposterAccademics)?.profile.lastName
                      }:`}{" "}
                      <i>{answer}</i>
                    </Typography>
                  </ListItem>
                  <ListItem className={classes.qaActions}>
                    <Button
                      variant="outlined"
                      onClick={onQuestionAnswerDialogOpen({
                        questionAnswer: { id, question, answer, published }
                      })}
                    >
                      <Edit />
                      {intl.formatMessage({
                        id: "Pages.EPosterPage.Edit",
                        defaultMessage: "Edit"
                      })}
                    </Button>
                    <Button variant="outlined" onClick={onQuestionAnswerDelete({ id })}>
                      <Delete />
                      {intl.formatMessage({
                        id: "Pages.EPosterPage.Delete",
                        defaultMessage: "Delete"
                      })}
                    </Button>
                  </ListItem>
                </div>
              ))}
            </NoQAData>
            <Paper>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Flaticon fontSize="1.5rem" icon="bubble-speak" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <strong>
                        {intl.formatMessage({
                          id: "Pages.EPosterPage.QuestionsWithoutAnswer",
                          defaultMessage: "Questions without answer"
                        })}
                      </strong>
                    }
                    primaryTypographyProps={{ variant: "h5" }}
                    secondary={
                      <>
                        {intl.formatMessage({
                          id: "Pages.EPosterPage.QuestionsWithoutAnswerHelper",
                          defaultMessage:
                            "Questions and comments sent to you will be publicly visible only if and when you decide to answer them."
                        })}
                        <Divider />
                      </>
                    }
                  />
                </ListItem>
                <NoQAData data={unpublishedQas}>
                  {unpublishedQas.map(({ id, question, answer, published }) => (
                    <React.Fragment key={id}>
                      <ListItem>
                        <ListItemIcon />
                        <Typography variant="body2" paragraph>
                          <i>{question}</i>
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.qaActions}>
                        <ListItemIcon />
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={onQuestionAnswerDialogOpen({
                            questionAnswer: { id, question, answer, published }
                          })}
                        >
                          <Reply />
                          {intl.formatMessage({
                            id: "Pages.EPosterPage.Reply",
                            defaultMessage: "Reply"
                          })}
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={onQuestionAnswerDelete({ id })}
                        >
                          <Delete />
                          {intl.formatMessage({
                            id: "Pages.EPosterPage.Delete",
                            defaultMessage: "Delete"
                          })}
                        </Button>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon />
                        <ListItemText primary={<Divider />} />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </NoQAData>
              </List>
            </Paper>
          </>
        ) : (
          <>
            <Formik initialValues={{ question: "" }} onSubmit={onQuestionAnswerCreate}>
              <Form>
                <TextField
                  name="question"
                  fullWidth
                  label={intl.formatMessage({
                    id: "Pages.EPosterPage.AddQuestion",
                    defaultMessage: "Add a question"
                  })}
                  variant="outlined"
                />
              </Form>
            </Formik>
            <NoQAData data={publishedQas}>
              {publishedQas.map(({ id, question, answer, profile }) => (
                <div key={id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar src={profile?.avatarThumb}>
                        <Flaticon icon="question" />
                      </Avatar>
                    </ListItemAvatar>
                    <Typography variant="body1">
                      {`${profile?.firstName} ${profile?.lastName}:`} <strong>{question}</strong>
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar src={_.first(ePoster.eposterAccademics)?.profile?.avatarThumb} />
                    </ListItemAvatar>
                    <Typography variant="body2">
                      {`${_.first(ePoster.eposterAccademics)?.profile.firstName} ${
                        _.first(ePoster.eposterAccademics)?.profile.lastName
                      }:`}{" "}
                      <i>{answer}</i>
                    </Typography>
                  </ListItem>
                </div>
              ))}
            </NoQAData>
          </>
        )}
      </Loader>
      <Divider />
      <Typography variant="h5">
        {intl.formatMessage({
          id: "Pages.EPosterPage.RelatedEPosters",
          defaultMessage: "Related e-Posters"
        })}
      </Typography>
      <EPostersGrid
        loading={relatedEPosterLoading}
        ePosters={congress.open ? relatedEPosters : []}
        truncateWords
      />
      {selectedQuestionAnswer && (
        <EditQuestionAnswerDialog
          questionAnswer={selectedQuestionAnswer}
          onClose={onQuestionAnswerDialogClose}
        />
      )}
    </BaseLayout>
  );
};

export default EPosterPage;
